/**
 * Low-level pure lexical helpers for the GRANT/REVOKE extractor (VDE-2
 * Slice 1). Split out of `grant.ts` to keep the orchestration core focused —
 * same sibling-split convention as `alterTable.clause.ts`.
 *
 * Regex-free (project rule VQE-D10): list/whitespace/quote scans are
 * character-level over already-isolated spans; identifier reads delegate to
 * `readIdentifier` / `readQualifiedName`.
 */

import type { ParseOptions } from '../../../sql-grammar/model';
import { readIdentifier, readQualifiedName } from '../../scope/identifiers';

import { skipWsCommentsLocal } from './shared';
import {
  GRANT_OBJECT_KIND_KEYWORDS,
  type ParsedGrantObject,
  type ParsedGrantObjectKind,
  type ParsedGrantPrincipal,
} from './grantTypes';

export interface Span {
  readonly s: number;
  readonly e: number;
}

/** Collapse internal whitespace runs in an already-isolated token to single
 *  spaces and upper-case it. Character-level only — not SQL structure. */
export function normalizePrivilege(raw: string): string {
  let out = '';
  let pendingSpace = false;
  for (const ch of raw.trim()) {
    if (ch === ' ' || ch === '\t' || ch === '\n' || ch === '\r') {
      pendingSpace = out.length > 0;
      continue;
    }
    if (pendingSpace) { out += ' '; pendingSpace = false; }
    out += ch;
  }
  return out.toUpperCase();
}

function isIdentStart(c: string | undefined): boolean {
  return c !== undefined && ((c >= 'A' && c <= 'Z') || (c >= 'a' && c <= 'z') || c === '_');
}

/** Skip a single-quoted string literal starting at `i` (which must be `'`).
 *  `terminated` reports whether a closing quote was found inside [i,end);
 *  on a missing close, `end` is returned with `terminated:false` so callers
 *  can reject the principal rather than silently truncate its last char. */
export function skipSingleQuoted(
  source: string,
  i: number,
  end: number,
): { end: number; terminated: boolean } {
  let j = i + 1;
  while (j < end) {
    if (source[j] === "'") {
      if (source[j + 1] === "'") { j += 2; continue; }
      return { end: j + 1, terminated: true };
    }
    j++;
  }
  return { end, terminated: false };
}

function trimmedNonEmpty(source: string, s: number, e: number): boolean {
  for (let i = s; i < e; i++) {
    const c = source[i];
    if (c !== ' ' && c !== '\t' && c !== '\n' && c !== '\r') return true;
  }
  return false;
}

function trimSpan(source: string, s: number, e: number): Span {
  let a = s;
  let b = e;
  while (a < b && (source[a] === ' ' || source[a] === '\t' || source[a] === '\n' || source[a] === '\r')) a++;
  while (b > a && (source[b - 1] === ' ' || source[b - 1] === '\t' || source[b - 1] === '\n' || source[b - 1] === '\r')) b--;
  return { s: a, e: b };
}

/** Split [start,end) into list segments at paren-depth-0 commas. This is a
 *  self-contained character-level scan (NOT a wrapper over, and not a mirror
 *  of, scanner.findTopLevelCommas): it advances over quoted identifiers via
 *  `readIdentifier` and over single-quoted `'…'` string literals via
 *  `skipSingleQuoted` (so mysql `'u'@'h'` grantees aren't split mid-literal),
 *  but it does NOT use `trySkipNonCode`, so mid-list line/block SQL comments
 *  are not specially handled here. */
export function splitListByTopCommas(
  source: string,
  start: number,
  end: number,
  options: ParseOptions,
): Span[] {
  const commas: number[] = [];
  let depth = 0;
  let i = start;
  while (i < end) {
    const ident = readIdentifier(source, i, end, options);
    if (ident && (ident.quoted || isIdentStart(source[i]))) { i = ident.end; continue; }
    const c = source[i];
    if (c === "'") { i = skipSingleQuoted(source, i, end).end; continue; }
    if (c === '(') { depth++; i++; continue; }
    if (c === ')') { if (depth > 0) depth--; i++; continue; }
    if (c === ',' && depth === 0) { commas.push(i); i++; continue; }
    i++;
  }
  const out: Span[] = [];
  let segStart = start;
  for (const c of commas) {
    out.push({ s: segStart, e: c });
    segStart = c + 1;
  }
  out.push({ s: segStart, e: end });
  return out.filter((seg) => trimmedNonEmpty(source, seg.s, seg.e));
}

export function parsePrincipal(
  source: string,
  s0: number,
  e0: number,
  options: ParseOptions,
): ParsedGrantPrincipal | null {
  const { s, e } = trimSpan(source, s0, e0);
  if (s >= e) return null;
  if (source.slice(s, e).toUpperCase() === 'PUBLIC') {
    return { kind: 'public', name: '', quoted: false };
  }
  if (source[s] === "'") {
    const nameQ = skipSingleQuoted(source, s, e);
    if (!nameQ.terminated) return null;
    const name = source.slice(s + 1, nameQ.end - 1);
    let k = nameQ.end;
    while (k < e && (source[k] === ' ' || source[k] === '\t')) k++;
    let mysqlHost: string | undefined;
    if (source[k] === '@') {
      k++;
      while (k < e && (source[k] === ' ' || source[k] === '\t')) k++;
      if (source[k] === "'") {
        const hostQ = skipSingleQuoted(source, k, e);
        if (!hostQ.terminated) return null;
        mysqlHost = source.slice(k + 1, hostQ.end - 1);
      }
    }
    return { kind: 'user', name, quoted: true, ...(mysqlHost ? { mysqlHost } : {}) };
  }
  const ident = readIdentifier(source, s, e, options);
  if (!ident) return null;
  return { kind: 'user', name: ident.value, quoted: ident.quoted };
}

function parseObjectName(
  source: string,
  s: number,
  e: number,
  options: ParseOptions,
): { name?: string; schema?: string; quoted: boolean; kind?: ParsedGrantObjectKind } | null {
  const first = readIdentifier(source, s, e, options);
  if (!first) return null;
  // `db.*` mysql all-tables-in-database shortcut.
  let k = skipWsCommentsLocal(source, first.end, e, options);
  if (source[k] === '.') {
    k = skipWsCommentsLocal(source, k + 1, e, options);
    if (source[k] === '*') {
      return { kind: 'database', name: first.value, quoted: first.quoted };
    }
  }
  const qn = readQualifiedName(source, s, e, options);
  if (!qn) return null;
  const { name, schema, quoted } = qn.qualified;
  return { name, ...(schema !== undefined ? { schema } : {}), quoted };
}

export function parseObject(
  source: string,
  s0: number,
  e0: number,
  options: ParseOptions,
): ParsedGrantObject | null {
  const { s, e } = trimSpan(source, s0, e0);
  if (s >= e) return null;

  // Optional <CLASS>:: prefix (mssql securable class) or <KIND> keyword.
  let kind: ParsedGrantObjectKind = 'table';
  let bodyStart = s;
  const lead = readIdentifier(source, s, e, options);
  if (lead && !lead.quoted) {
    const upper = lead.value.toUpperCase();
    if (source[lead.end] === ':' && source[lead.end + 1] === ':') {
      kind = GRANT_OBJECT_KIND_KEYWORDS[upper] ?? 'table';
      bodyStart = skipWsCommentsLocal(source, lead.end + 2, e, options);
    } else if (GRANT_OBJECT_KIND_KEYWORDS[upper]) {
      const afterKw = skipWsCommentsLocal(source, lead.end, e, options);
      if (afterKw < e) {
        kind = GRANT_OBJECT_KIND_KEYWORDS[upper]!;
        bodyStart = afterKw;
      }
    }
  }

  const named = parseObjectName(source, bodyStart, e, options);
  if (!named) return null;
  return { ...named, kind: named.kind ?? kind };
}
